import type { LocationQuery } from 'vue-router';

import type { SelectedEntityInput, UnifiedSavedEntityType } from '~/graphql/generated/graphql';

export const SELECTED_ENTITY_QUERY_PARAMS = ['selectedEntityType', 'selectedEntityId', 'selectedEntityName', 'selectedEntitySlug', 'selectedEntity'] as const;

// btoa/atob only handle Latin-1, so entity names with curly quotes, em dashes or
// emoji must round-trip through UTF-8 bytes (CP-10764).
const toBase64 = (value: string): string => btoa(String.fromCharCode(...new TextEncoder().encode(value)));

// Legacy values base64'd raw code units, so an accented Latin-1 name is invalid UTF-8;
// a strict decode failure identifies one, and it's decoded byte-per-char.
const fromBase64 = (encoded: string): string => {
  const bytes = Uint8Array.from(atob(encoded), c => c.charCodeAt(0));
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(bytes);
  }
  catch {
    return String.fromCharCode(...bytes);
  }
};

export const buildSelectedEntityQueryString = ({ entityType, entityId, entityName, entitySlug }: SelectedEntityInput): string => {
  const encoded = toBase64(JSON.stringify({ entityType, entityId, entityName, entitySlug }));
  return new URLSearchParams({ selectedEntity: encoded }).toString();
};

export const pickSelectedEntityQueryParams = (query: LocationQuery): Record<string, string> => {
  const params: Record<string, string> = {};
  for (const param of SELECTED_ENTITY_QUERY_PARAMS) {
    const value = query[param];
    if (typeof value === 'string') {
      params[param] = value;
    }
  }
  return params;
};

const parseCompactSelectedEntity = (encoded: string): SelectedEntityInput | null => {
  try {
    const parsed = JSON.parse(fromBase64(encoded));
    if (parsed.entityType && parsed.entityId && parsed.entityName) {
      return {
        entityType: parsed.entityType as UnifiedSavedEntityType,
        entityId: parsed.entityId,
        entityName: parsed.entityName,
        entitySlug: typeof parsed.entitySlug === 'string' ? parsed.entitySlug : undefined,
      };
    }
    return null;
  }
  catch {
    return null;
  }
};

export const parseSelectedEntityFromQuery = (query: LocationQuery): SelectedEntityInput | null => {
  const selectedEntity = typeof query.selectedEntity === 'string' ? query.selectedEntity : null;
  if (selectedEntity) {
    return parseCompactSelectedEntity(selectedEntity);
  }

  const entityType = typeof query.selectedEntityType === 'string' ? query.selectedEntityType : null;
  const entityId = typeof query.selectedEntityId === 'string' ? query.selectedEntityId : null;
  const entityName = typeof query.selectedEntityName === 'string' ? query.selectedEntityName : null;
  const entitySlug = typeof query.selectedEntitySlug === 'string' ? query.selectedEntitySlug : undefined;

  if (!entityType || !entityId || !entityName) {
    return null;
  }

  return {
    entityType: entityType as UnifiedSavedEntityType,
    entityId,
    entityName,
    entitySlug,
  };
};
